import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2, Package, XCircle } from "lucide-react";

export default function FrameConfirmation() {
  const { orderNumber } = useParams();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/frames/orders/${orderNumber}`)
      .then((r) => setOrder(r.data))
      .catch(() => setNotFound(true));
  }, [orderNumber]);

  if (notFound) {
    return (
      <div className="pt-40 max-w-lg mx-auto text-center relative z-10">
        <XCircle className="w-10 h-10 text-red-400 mx-auto" />
        <h1 className="font-display text-3xl font-bold mt-4">Order not found</h1>
        <Link to="/frames" className="btn-primary mt-6">Back to Frames</Link>
      </div>
    );
  }
  if (!order) {
    return (
      <div className="pt-40 flex justify-center relative z-10">
        <Loader2 className="w-6 h-6 animate-spin text-gold" />
      </div>
    );
  }

  return (
    <div className="pt-28 pb-24 px-6 lg:px-10 relative z-10">
      <div className="max-w-3xl mx-auto text-center">
        <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
        <div className="overline text-gold mt-6">Order Confirmed</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2" data-testid="frame-confirm-title">
          Your frame is on the way, {order.customer_name.split(" ")[0]}.
        </h1>
        <p className="text-white/60 mt-3 max-w-xl mx-auto">
          Thank you for your purchase. We'll hand-inspect the frame and ship it within 5–7 business days.
          Save your order number to track it later.
        </p>

        <div className="mt-10 glass rounded-3xl p-8 text-left" data-testid="frame-confirm-card">
          <div className="grid sm:grid-cols-[220px_1fr] gap-6 items-start">
            <div className="rounded-2xl overflow-hidden bg-black/40 aspect-[3/4]">
              <img src={order.frame_image_url} alt={order.frame_planet} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="overline text-gold">Order Number</div>
              <div className="font-mono-space text-2xl text-gold mt-1" data-testid="frame-order-number">{order.order_number}</div>

              <div className="mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
                <Item k="Frame" v={order.frame_planet} />
                <Item k="Amount Paid" v={`₹${order.amount_inr}`} mono />
                <Item k="Payment" v={<span className="text-emerald-300">Paid</span>} />
                <Item k="Order Status" v={<span className="text-gold uppercase font-mono-space text-sm">{order.order_status}</span>} />
                <Item
                  k="Order Date"
                  v={new Date(order.purchase_date).toLocaleString()}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to="/frames" className="btn-ghost" data-testid="frame-confirm-more">
            <Package className="w-4 h-4" /> Browse More Frames
          </Link>
          <Link to="/" className="btn-primary" data-testid="frame-confirm-home">Return Home</Link>
        </div>

        <div className="mt-10 text-xs text-white/45 max-w-lg mx-auto">
          Keep your order number safe. Our team will reach out on your mobile with dispatch and tracking details.
        </div>
      </div>
    </div>
  );
}

function Item({ k, v, mono }) {
  return (
    <div>
      <div className="overline">{k}</div>
      <div className={`mt-1 ${mono ? "font-mono-space text-sm" : "text-base"} text-white`}>{v}</div>
    </div>
  );
}
